import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const LaunchSchedule = () => {
  const [launches, setLaunches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providerFilter, setProviderFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [selectedLaunch, setSelectedLaunch] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [providerLogos, setProviderLogos] = useState({ "default": "https://placehold.co/50x50?text=Logo" });
  const [rocketImages, setRocketImages] = useState({ "default": "https://placehold.co/100x100?text=Rocket" });
  const [view, setView] = useState('list');

  const fetchLaunches = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://lldev.thespacedevs.com/2.2.0/launch/upcoming?limit=50&include_suborbital=true");
      const launchesData = response.data.results || [];
      console.log("Fetched launches:", launchesData.map(l => ({ name: l.name, provider: l.launch_service_provider?.name, rocket: l.rocket?.configuration?.name }))); // Debug provider and rocket names
      setLaunches(launchesData);
    } catch (error) {
      console.error("Error fetching launches:", error);
      alert("Error fetching launches. Check console for details.");
      setLaunches([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLaunches();
  }, []);

  useEffect(() => {
    if (selectedLaunch && selectedLaunch.net) {
      const updateCountdown = () => {
        const now = new Date().getTime();
        const launchTime = new Date(selectedLaunch.net).getTime();
        const distance = launchTime - now;

        if (distance <= 0) {
          setCountdown("Launch has occurred!");
        } else {
          const days = Math.floor(distance / (1000 * 60 * 60 * 24));
          const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((distance % (1000 * 60)) / 1000);
          setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
        }
      };
      const interval = setInterval(updateCountdown, 1000);
      updateCountdown();
      return () => clearInterval(interval);
    }
  }, [selectedLaunch]);

  const filteredLaunches = launches.filter((launch) => {
    const providerMatch = !providerFilter || 
      (launch.launch_service_provider?.name?.toLowerCase()?.includes(providerFilter.toLowerCase()) ?? false);
    const locationMatch = !locationFilter || 
      (launch.pad?.location?.name?.toLowerCase()?.includes(locationFilter.toLowerCase()) ?? false);
    return providerMatch && locationMatch;
  });

  const closeModal = () => setSelectedLaunch(null);

  const fetchImages = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "launchImages"));
      const providers = { "default": "https://placehold.co/50x50?text=Logo" };
      const rockets = { "default": "https://placehold.co/100x100?text=Rocket" };
      querySnapshot.forEach(doc => {
        const data = doc.data();
        if (data.type === "provider") {
          providers[data.name] = data.url;
        } else if (data.type === "rocket") {
          rockets[data.name] = data.url;
        }
      });
      setProviderLogos(providers);
      setRocketImages(rockets);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const getLaunchesForDate = (date) => {
    const dateStr = date.toDateString();
    return filteredLaunches.filter(launch => {
      if (!launch.net) return false;
      const launchDate = new Date(launch.net);
      return launchDate.toDateString() === dateStr;
    });
  };



  return (
    <div className="min-h-screen bg-[var(--container-bg)] p-4 max-w-none mx-auto">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-4">Launch Schedule</h2>
        <div className="mb-4 flex justify-center space-x-4">
          <button
            onClick={() => setView('list')}
            className={`px-4 py-2 rounded-custom border ${view === 'list' ? 'bg-kids-blue text-gray-800 border-kids-blue' : 'bg-transparent text-gray-100 border-gray-500 hover:bg-gray-700'}`}
          >
            List View
          </button>
          <button
            onClick={() => setView('calendar')}
            className={`px-4 py-2 rounded-custom border ${view === 'calendar' ? 'bg-kids-blue text-gray-800 border-kids-blue' : 'bg-transparent text-gray-100 border-gray-500 hover:bg-gray-700'}`}
          >
            Calendar View
          </button>
        </div>
        <div className="mb-4 flex space-x-4">
          <input
            type="text"
            placeholder="Filter by Provider (e.g., SpaceX)"
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="p-2 rounded-custom bg-gray-700 text-gray-100 flex-1"
          />
          <input
            type="text"
            placeholder="Filter by Location (e.g., Cape Canaveral)"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="p-2 rounded-custom bg-gray-700 text-gray-100 flex-1"
          />
        </div>
      </div>
      {view === 'list' ? (
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
          {loading ? (
            <p className="text-gray-100">Loading launches...</p>
          ) : filteredLaunches.length === 0 ? (
            <p className="text-gray-100">No launches found. Try adjusting filters or check back later.</p>
          ) : (
            filteredLaunches.map((launch) => (
              <div key={launch.id} className="bg-silver p-4 rounded-custom shadow-lg flex items-center" style={{ height: "250px" }}>
                <div className="w-24 h-24 mr-4 flex-shrink-0">
                  <img
                    src={providerLogos[launch.launch_service_provider?.name] || providerLogos["default"]}
                    alt={`${launch.launch_service_provider?.name}`}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.src = providerLogos["default"]; console.log("Fallback to default logo for:", launch.launch_service_provider?.name); }}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{launch.name || "Unnamed Launch"}</h3>
                  <p className="text-gray-700 mb-1">Provider: {launch.launch_service_provider?.name ?? "N/A"}</p>
                  <p className="text-gray-700 mb-1">Location: {launch.pad?.location?.name ?? "N/A"}</p>
                  <p className="text-gray-700">
                    Date: {launch.net ? (
                      new Date(launch.net) < new Date() ? (
                        <span className="text-red-600 font-bold">Launched!</span>
                      ) : (
                        new Date(launch.net).toLocaleString("en-GB")
                      )
                    ) : "TBA"}
                  </p>
                  <p className="text-gray-700">
                    Mission: {launch.mission?.description?.slice(0, 100) || launch.mission_description?.slice(0, 100) || "N/A"}...
                  </p>
                  <div className="mt-2 flex flex-row space-x-2">
                    <button
                      onClick={() => setSelectedLaunch(launch)}
                      className="flex-1 bg-kids-blue text-gray-800 px-4 py-2 rounded-custom hover:bg-blue-600"
                    >
                      View Mission Info
                    </button>
                    <button
                      onClick={() => {
                        // Navigate to globe visualizer in trajectory mode
                        window.location.href = `/globe?mode=trajectory&trajectory=${encodeURIComponent(JSON.stringify({
                          name: launch.name,
                          rocket: launch.rocket?.configuration?.name,
                          pad: launch.pad,
                          targetOrbit: { altitude: 408000, inclination: 28.5 }
                        }))}`;
                      }}
                      className="flex-1 bg-orange-500 text-white px-4 py-2 rounded-custom hover:bg-orange-600"
                    >
                      🚀 View Trajectory (beta)
                    </button>
                  </div>
                  {launch.vidURLs && launch.vidURLs.length > 0 && (
                    <a
                      href={launch.vidURLs[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-kids-blue hover:underline mt-2 inline-block"
                    >
                      Watch Livestream
                    </a>
                  )}
                </div>
                <div className="w-[125px] h-full ml-4 flex-shrink-0 self-stretch pt-0 pb-0 pr-0 overflow-hidden">
                  <img
                    src={rocketImages[launch.rocket?.configuration?.name] || rocketImages["default"]}
                    alt={`${launch.rocket?.configuration?.name}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = rocketImages["default"]; console.log("Fallback to default rocket for:", launch.rocket?.configuration?.name); }}
                  />
                </div>
              </div>
            ))
          )}
          </div>
        </div>
      ) : (
        <div className="flex justify-center px-4">
          <Calendar
            tileContent={({ date, view }) => {
              if (view === 'month') {
                const launchesOnDate = getLaunchesForDate(date);
                if (launchesOnDate.length > 0) {
                  return (
                    <div className="flex flex-col space-y-1 mt-2">
                      {launchesOnDate.map((launch) => (
                        <div
                          key={launch.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLaunch(launch);
                          }}
                          className="text-xs bg-kids-blue text-white px-1 py-0.5 rounded truncate hover:bg-blue-600 cursor-pointer"
                        >
                          {launch.launch_service_provider?.name || 'Unknown'}
                        </div>
                      ))}
                    </div>
                  );
                }
              }
              return null;
            }}
            className="react-calendar calendar-wide"
          />
        </div>
      )}

      {selectedLaunch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-silver p-6 rounded-custom shadow-lg w-11/12 md:w-2/3 lg:w-1/2">
            <h3 className="text-xl font-bold text-gray-800 mb-2">{selectedLaunch.name || "Unnamed Launch"}</h3>
            <p className="text-gray-700">Provider: {selectedLaunch.launch_service_provider?.name ?? "N/A"}</p>
            <p className="text-gray-700">Location: {selectedLaunch.pad?.location?.name ?? "N/A"}</p>
            <p className="text-gray-700">Date: {selectedLaunch.net ? new Date(selectedLaunch.net).toLocaleString("en-GB") : "TBA"}</p>
            <p className="text-gray-700 mb-2">Mission: {selectedLaunch.mission?.description || selectedLaunch.mission_description || "No mission details available"}</p>
            <p className="text-gray-700 mb-2">Countdown: {countdown}</p>
            {selectedLaunch.vidURLs && selectedLaunch.vidURLs.length > 0 && (
              <a
                href={selectedLaunch.vidURLs[0]}
                target="_blank"
                rel="noopener noreferrer"
                className="text-kids-blue hover:underline mb-2 inline-block"
              >
                Watch Livestream
              </a>
            )}
            <button
              onClick={closeModal}
              className="mt-4 bg-kids-blue text-gray-800 px-4 py-2 rounded-custom hover:bg-blue-600"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaunchSchedule;
